// device namespace (window.backlit.device) — a small, device-only store for
// UI preferences (a Light / Dark / Auto theme, say). Never app data: values
// stay on this device, are never synced, and are tightly capped (D-DEV2).
// Author code can't use localStorage / indexedDB (the deploy scan bans them),
// and userdata roams with the user, so this is the one sanctioned carve-out.
//
// Design notes:
//   - Lazy: nothing opens and no listener registers until the first call, so
//     the SDK load path is unchanged. Works without offline.enable().
//   - Its own database, backlit-device (stores `values` and `meta`), separate
//     from the offline mirror and outbox: offline.clear() and the service
//     worker never touch it; the edge logout page deletes it by name (the
//     must-match with cmd/edge/setcookie.go deviceDBName).
//   - One transaction per operation (no held connection), run on a per-page
//     queue. A VersionError (a newer build's schema) latches `unsupported` and
//     never deletes anything.
//   - Owner-tagged: meta.owner is an IdentityRecord (uid "" = anonymous). A
//     /ui answer or page proof naming someone else, a sign-out or an auth-mode
//     change wipes the values and retags the owner in one transaction;
//     anonymous values are adopted (retagged, not wiped) at sign-in (D-DEV3).
//   - A user-owned value is served only once this page has proven that user
//     (/ui answer, an X-Backlit-UID response, a uid heartbeat), or after a real
//     transport failure of /ui while the owner is fresh (!isStale; a stale one
//     is wiped). While /ui is merely pending a call waits up to 10 s, then gets
//     null (a read) or `network` (a write) — neither served nor wiped (R2-2).

import type { UIConfig } from "./backend.js";
import { BacklitError } from "./errors.js";
import { onHeartbeatResult } from "./heartbeat.js";
import { assertOnBacklitHost } from "./host.js";
import { encodeJSON } from "./json.js";
import {
  applyUIConfig,
  emptyIdentity,
  isStale,
  normalizeIdentity,
  now,
  type IdentityRecord,
} from "./offline-identity.js";
import { OfflineStoreError, idbHandle, type IdbEnv, type IdbHandle } from "./offline-store.js";
import {
  addFailureObserver,
  addResponseObserver,
  getUIConfigMemo,
  onUIConfig,
  remoteBackend,
  responseMetaOf,
} from "./transport.js";
import { validateDataKey } from "./validate.js";

export const DEVICE_DB_NAME = "backlit-device";
export const DEVICE_DB_VERSION = 1;

// Caps (D-DEV2). Totals count key + value bytes (UTF-8); an overwrite is
// measured by its delta.
export const DEVICE_MAX_VALUE_BYTES = 8192;
export const DEVICE_MAX_KEYS = 64;
export const DEVICE_MAX_TOTAL_BYTES = 65_536;

// DEVICE_WAIT_MS bounds how long a call waits for this page to learn who is
// signed in before a user-owned value is refused.
export const DEVICE_WAIT_MS = 10_000;

const STORE_VALUES = "values";
const STORE_META = "meta";
const META_OWNER = "owner";
const UI_PATH = "/_backlit/ui";

// DeviceNamespace mirrors sdk.d.ts.
export interface DeviceNamespace {
  get(key: string): Promise<string | null>;
  put(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
  getJSON<T = unknown>(key: string): Promise<T | null>;
  putJSON(key: string, value: unknown): Promise<void>;
}

// --- page state -----------------------------------------------------------------

// pageUid is who this page itself has learned is signed in: null = nothing yet,
// "" = its /ui answer said signed out. pageAt / pageDate are when it learned
// (local clock) and the answer's server Date; pageAuthMode is the last /ui
// answer's auth mode ("" = none yet). uiFailed: this page's /ui got no answer.
let pageUid: string | null = null;
let pageAuthMode = "";
let pageAt = 0;
let pageDate = 0;
let uiFailed = false;
// ownerUid caches meta.owner.uid as of the last transaction (null = not read).
let ownerUid: string | null = null;
let started = false;
let unlisten: Array<() => void> = [];
let handle: IdbHandle | null = null;
let queue: Promise<unknown> = Promise.resolve();
const waiters = new Set<() => void>();

interface TestEnv {
  // idb: the IndexedDB to use (null = none); omitted = the page's.
  idb?: IdbEnv | null;
  // waitMs replaces DEVICE_WAIT_MS.
  waitMs?: number;
}
let testEnv: TestEnv | null = null;

// start registers the identity listeners once (the first device call) and
// makes sure this page asks /ui: the memo when there is one, else a request
// (single-flight — it joins one already on the wire).
function start(): void {
  if (started) return;
  started = true;
  unlisten.push(
    onUIConfig(onUI),
    addResponseObserver((m) => {
      if (m.status >= 200 && m.status < 300 && m.uid !== "") onProof(m.uid, m.date);
    }),
    addFailureObserver((path) => {
      if (path === UI_PATH) onUIFailed();
    }),
    onHeartbeatResult((status, info) => {
      if (status >= 200 && status < 300 && info.uid !== "") onProof(info.uid, info.date);
    }),
  );
  const w = (globalThis as { window?: EventTarget }).window;
  if (w && typeof w.addEventListener === "function") {
    w.addEventListener("pageshow", onPageShow);
    unlisten.push(() => w.removeEventListener("pageshow", onPageShow));
  }
  const memo = getUIConfigMemo();
  if (memo) onUI(memo);
  else askUI();
}

// askUI fetches /ui; its answer arrives through onUIConfig, a transport
// failure marks uiFailed.
function askUI(): void {
  remoteBackend.uiConfig().catch((err: unknown) => {
    if (err instanceof BacklitError && err.code === "network") onUIFailed();
  });
}

function onUI(cfg: UIConfig): void {
  if (cfg.authMode !== "") pageAuthMode = cfg.authMode;
  const date = responseMetaOf(cfg)?.date ?? 0;
  if (cfg.user === null) learn("", date);
  else if (cfg.user.uid !== "") learn(cfg.user.uid, date);
  else settleSoon();
}

// onProof: a response this page received named uid.
function onProof(uid: string, date: number): void {
  if (pageUid !== uid) learn(uid, date);
}

function onUIFailed(): void {
  uiFailed = true;
  wake();
  settleSoon();
}

// onPageShow: a bfcache restore drops what this page learned and re-checks.
function onPageShow(e: Event): void {
  if ((e as PageTransitionEvent).persisted !== true) return;
  pageUid = null;
  uiFailed = false;
  askUI();
}

function learn(uid: string, date: number): void {
  pageUid = uid;
  pageAt = now();
  pageDate = date;
  wake();
  settleSoon();
}

function wake(): void {
  for (const w of Array.from(waiters)) w();
}

// waitIdentity resolves once this page learns who is signed in (or that /ui
// failed), or after the wait bound.
function waitIdentity(): Promise<void> {
  return new Promise<void>((resolve) => {
    const done = () => {
      clearTimeout(t);
      waiters.delete(done);
      resolve();
    };
    const t = setTimeout(done, testEnv?.waitMs ?? DEVICE_WAIT_MS);
    (t as { unref?: () => void }).unref?.();
    waiters.add(done);
  });
}

// --- the store ------------------------------------------------------------------

function upgrade(db: IDBDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    db.createObjectStore(STORE_VALUES);
    db.createObjectStore(STORE_META);
  }
}

function db(): IdbHandle {
  if (!handle) handle = idbHandle(testEnv?.idb, DEVICE_DB_NAME, DEVICE_DB_VERSION, upgrade);
  return handle;
}

// enqueue runs task after every task queued before it.
function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const p = queue.then(task);
  queue = p.then(
    () => undefined,
    () => undefined,
  );
  return p;
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

// Settled is the owner decision for one transaction: the record to keep, whether
// the values must be wiped first, and whether this page may use them.
interface Settled {
  record: IdentityRecord;
  wipe: boolean;
  serve: boolean;
}

// settle decides what this page's knowledge does to the stored owner.
function settle(owner: IdentityRecord, at: number): Settled {
  if (pageUid !== null) {
    const user = pageUid === "" ? null : { uid: pageUid, permission: "" as const };
    const res = applyUIConfig(owner, { authMode: pageAuthMode, user }, pageDate, pageAt);
    return { record: res.record, wipe: res.action !== "none", serve: true };
  }
  const reset = () => ({ ...emptyIdentity(), authMode: pageAuthMode || owner.authMode, gen: owner.gen + 1 });
  if (pageAuthMode !== "" && owner.authMode !== "" && owner.authMode !== pageAuthMode) {
    return { record: reset(), wipe: true, serve: true };
  }
  if (owner.uid === "") return { record: owner, wipe: false, serve: true };
  if (uiFailed) {
    if (isStale(owner, at)) return { record: reset(), wipe: true, serve: true };
    return { record: owner, wipe: false, serve: true };
  }
  return { record: owner, wipe: false, serve: false };
}

// withOwner runs body in one readwrite transaction after settling the owner
// (wipe + retag as needed); body sees whether it may use the values.
function withOwner<T>(body: (values: IDBObjectStore, serve: boolean) => Promise<T>): Promise<T> {
  let uid = "";
  return db()
    .run([STORE_VALUES, STORE_META], "readwrite", async (tx) => {
      const meta = tx.objectStore(STORE_META);
      const values = tx.objectStore(STORE_VALUES);
      const owner = normalizeIdentity(await req(meta.get(META_OWNER))) ?? emptyIdentity();
      const s = settle(owner, now());
      if (s.wipe) values.clear();
      if (s.wipe || JSON.stringify(s.record) !== JSON.stringify(owner)) meta.put(s.record, META_OWNER);
      uid = s.record.uid;
      return body(values, s.serve);
    })
    .then(
      (v) => {
        ownerUid = uid;
        return v;
      },
      (err: unknown) => {
        throw storeError(err);
      },
    );
}

// settleSoon applies what the page just learned to the stored owner (a wipe
// happens here, not on the next read). Failures are silent: the next call
// reports them.
function settleSoon(): void {
  enqueue(() => withOwner(async () => undefined)).catch(() => undefined);
}

function storeError(err: unknown): BacklitError {
  if (err instanceof BacklitError) return err;
  if (err instanceof OfflineStoreError && err.detail === "QuotaExceededError") {
    return new BacklitError("quota_exceeded", "the device store is full", 0, err);
  }
  return new BacklitError("unsupported", "on-device storage is not available in this browser", 0, err);
}

// ready starts the listeners, reads the owner once, and — when the stored
// values belong to a user this page hasn't learned about yet — waits for /ui.
async function ready(): Promise<void> {
  start();
  if (ownerUid === null) await enqueue(() => withOwner(async () => undefined));
  if (ownerUid !== "" && pageUid === null && !uiFailed) await waitIdentity();
}

function utf8Len(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length && (s.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
      n += 4;
      i++;
    } else n += 3;
  }
  return n;
}

function unconfirmed(): BacklitError {
  return new BacklitError("network", "could not confirm the signed-in user in time; try again", 0);
}

async function read(key: string): Promise<string | null> {
  await ready();
  return enqueue(() =>
    withOwner(async (values, serve) => {
      if (!serve) return null;
      const v = await req(values.get(key));
      return typeof v === "string" ? v : null;
    }),
  );
}

async function write(key: string, value: string): Promise<void> {
  await ready();
  const out = await enqueue(() =>
    withOwner(async (values, serve): Promise<"ok" | "keys" | "total" | "unconfirmed"> => {
      if (!serve) return "unconfirmed";
      const keys = (await req(values.getAllKeys())) as IDBValidKey[];
      const vals = (await req(values.getAll())) as unknown[];
      let total = 0;
      let old = -1;
      for (let i = 0; i < keys.length; i++) {
        const k = String(keys[i]);
        const size = k.length + (typeof vals[i] === "string" ? utf8Len(vals[i] as string) : 0);
        if (k === key) old = size;
        total += size;
      }
      if (old < 0 && keys.length >= DEVICE_MAX_KEYS) return "keys";
      if (total - Math.max(old, 0) + key.length + utf8Len(value) > DEVICE_MAX_TOTAL_BYTES) return "total";
      values.put(value, key);
      return "ok";
    }),
  );
  if (out === "unconfirmed") throw unconfirmed();
  if (out === "keys") throw new BacklitError("quota_exceeded", `the device store holds at most ${DEVICE_MAX_KEYS} keys`, 0);
  if (out === "total") {
    throw new BacklitError("quota_exceeded", `the device store holds at most ${DEVICE_MAX_TOTAL_BYTES} bytes`, 0);
  }
}

async function remove(key: string): Promise<void> {
  await ready();
  const ok = await enqueue(() =>
    withOwner(async (values, serve) => {
      if (serve) values.delete(key);
      return serve;
    }),
  );
  if (!ok) throw unconfirmed();
}

// createDevice returns the device namespace. Every method rejects with
// BacklitError; nothing is opened until the first call.
export function createDevice(): DeviceNamespace {
  const ns: DeviceNamespace = {
    async get(key: string): Promise<string | null> {
      validateDataKey(key);
      assertOnBacklitHost();
      return read(key);
    },

    async put(key: string, value: string): Promise<void> {
      validateDataKey(key);
      if (typeof value !== "string") throw new BacklitError("invalid_argument", "value must be a string");
      if (utf8Len(value) > DEVICE_MAX_VALUE_BYTES) {
        throw new BacklitError("payload_too_large", `a device value is at most ${DEVICE_MAX_VALUE_BYTES} bytes`, 0);
      }
      assertOnBacklitHost();
      return write(key, value);
    },

    async delete(key: string): Promise<void> {
      validateDataKey(key);
      assertOnBacklitHost();
      return remove(key);
    },

    async getJSON<T = unknown>(key: string): Promise<T | null> {
      const v = await ns.get(key);
      if (v === null) return null;
      try {
        return JSON.parse(v) as T;
      } catch (e) {
        throw new BacklitError("invalid_body", `the value stored at "${key}" is not valid JSON`, 0, e);
      }
    },

    async putJSON(key: string, value: unknown): Promise<void> {
      return ns.put(key, encodeJSON(value));
    },
  };
  return ns;
}

// --- test hooks -------------------------------------------------------------------

// _setDeviceEnvForTest injects the IndexedDB (and the wait bound); null restores
// the defaults. Call _resetDeviceForTest first.
export function _setDeviceEnvForTest(e: TestEnv | null): void {
  testEnv = e;
  handle = null;
}

// _resetDeviceForTest forgets everything this page learned (a fresh page load):
// listeners, waiters (released) and their timers, the queue and the handle.
export function _resetDeviceForTest(): void {
  for (const u of unlisten) u();
  unlisten = [];
  wake();
  started = false;
  pageUid = null;
  pageAuthMode = "";
  pageAt = 0;
  pageDate = 0;
  uiFailed = false;
  ownerUid = null;
  handle = null;
  queue = Promise.resolve();
}

// _idleDeviceForTest resolves once every queued device task has run.
export async function _idleDeviceForTest(): Promise<void> {
  for (;;) {
    const q = queue;
    await q;
    if (q === queue) return;
  }
}
